import React, { useEffect, useState, useRef } from 'react';
import { Search, MapPin, BedDouble, Bath, DollarSign, ChevronDown, Check } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
interface DropdownOption {
  value: string;
  label: string;
}
function SearchDropdown({
  icon: Icon,
  placeholder,
  options,
  value,
  onChange,
  isOpen,
  onToggle,
  onClose
}: {
  icon: React.ElementType;
  placeholder: string;
  options: DropdownOption[];
  value: string;
  onChange: (val: string) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const selectedLabel = options.find(o => o.value === value)?.label;
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (isOpen && ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  return <div ref={ref} className="relative w-full md:w-auto" data-id="element-292">
      <button type="button" onClick={onToggle} className="flex items-center gap-2 h-14 px-4 w-full md:w-auto text-base rounded-xl transition-all duration-200 hover:bg-muted active:scale-[0.98]" data-id="element-293">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" data-id="element-294" />
        <span className={selectedLabel ? 'text-foreground font-medium' : 'text-muted-foreground'} data-id="element-295">
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground ml-auto transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-296" />
      </button>

      {isOpen && <div className="absolute top-full left-0 mt-2 z-50 min-w-[200px] bg-white rounded-xl p-1.5 animate-in fade-in-0 zoom-in-95 duration-150" style={{
      boxShadow: '0 20px 60px -12px rgba(0,0,0,0.15), 0 8px 20px -8px rgba(0,0,0,0.08)'
    }} data-id="element-297">
          {options.map(option => {
        const isSelected = value === option.value;
        return <button key={option.value} type="button" onClick={() => {
          onChange(option.value);
          onClose();
        }} className={`flex items-center justify-between w-full text-left px-3.5 py-3 rounded-lg text-sm font-medium cursor-pointer transition-all duration-150
                  ${isSelected ? 'bg-primary text-primary-foreground shadow-sm' : 'text-foreground hover:bg-muted hover:translate-x-0.5'}`} data-id="element-298">
                {option.label}
                {isSelected && <Check className="h-4 w-4 shrink-0" data-id="element-299" />}
              </button>;
      })}
        </div>}
    </div>;
}
const bedOptions: DropdownOption[] = [{
  value: 'studio',
  label: 'Studio'
}, {
  value: '1',
  label: '1 Bedroom'
}, {
  value: '2',
  label: '2 Bedrooms'
}, {
  value: '3',
  label: '3 Bedrooms'
}, {
  value: '4',
  label: '4+ Bedrooms'
}];
const bathOptions: DropdownOption[] = [{
  value: '1',
  label: '1 Bathroom'
}, {
  value: '1.5',
  label: '1.5 Bathrooms'
}, {
  value: '2',
  label: '2 Bathrooms'
}, {
  value: '3',
  label: '3+ Bathrooms'
}];
const priceOptions: DropdownOption[] = [{
  value: '0-500',
  label: 'Under $500'
}, {
  value: '500-1000',
  label: '$500 – $1,000'
}, {
  value: '1000-1500',
  label: '$1,000 – $1,500'
}, {
  value: '1500-2000',
  label: '$1,500 – $2,000'
}, {
  value: '2000+',
  label: '$2,000+'
}];
export function SearchBar() {
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');
  const [price, setPrice] = useState('');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const toggle = (name: string) => {
    setOpenDropdown(prev => prev === name ? null : name);
  };
  const close = () => setOpenDropdown(null);
  return <div className="relative z-20 -mt-12 mb-8 container mx-auto px-4" data-id="element-300">
      <div className="bg-white rounded-2xl shadow-[0_8px_50px_-6px_rgba(0,0,0,0.15)] border border-border/30 p-4 ring-1 ring-black/[0.03]" data-id="element-301">
        <div className="flex flex-col md:flex-row items-stretch" data-id="element-302">
          {/* Location input */}
          <div className="relative flex-1" data-id="element-303">
            <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" data-id="element-304" />
            <Input placeholder="City, zip code, or neighborhood..." className="pl-12 h-14 border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 text-base rounded-xl shadow-none" onFocus={close} data-id="element-305" />
          </div>

          <div className="hidden md:block w-px bg-border self-stretch my-3" data-id="element-306" />

          <SearchDropdown icon={BedDouble} placeholder="Beds" options={bedOptions} value={beds} onChange={setBeds} isOpen={openDropdown === 'beds'} onToggle={() => toggle('beds')} onClose={close} data-id="element-307" />

          <div className="hidden md:block w-px bg-border self-stretch my-3" data-id="element-308" />

          <SearchDropdown icon={Bath} placeholder="Baths" options={bathOptions} value={baths} onChange={setBaths} isOpen={openDropdown === 'baths'} onToggle={() => toggle('baths')} onClose={close} data-id="element-309" />

          <div className="hidden md:block w-px bg-border self-stretch my-3" data-id="element-310" />

          <SearchDropdown icon={DollarSign} placeholder="Price Range" options={priceOptions} value={price} onChange={setPrice} isOpen={openDropdown === 'price'} onToggle={() => toggle('price')} onClose={close} data-id="element-311" />

          <Button className="h-14 px-8 rounded-xl gap-2 text-base font-medium shadow-md m-0.5" onClick={close} data-id="element-312">
            <Search className="h-5 w-5" data-id="element-313" />
            Search
          </Button>
        </div>
      </div>
    </div>;
}